import React, { useState, useEffect, useRef } from 'react';
import { DeckGL } from 'deck.gl';
import { StaticMap } from 'react-map-gl';
import { WebMercatorViewport } from 'viewport-mercator-project';
import { ScatterplotLayer, GeoJsonLayer, ArcLayer } from '@deck.gl/layers';
import {
  GridLayer,
  HexagonLayer,
  HeatmapLayer,
} from '@deck.gl/aggregation-layers';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FlyToInterpolator } from 'deck.gl';

function MapView({
  data,
  countryGeoJson,
  onZoomChange,
  santaLocations,
  heatmapData,
  tileZoom,
  onMapChange,
  flyTo, // Add flyTo prop
}) {
  const [snowflakes, setSnowflakes] = useState([]);
  const [viewState, setViewState] = useState({
    longitude: 0,
    latitude: 40,
    zoom: 2,
    pitch: 30,
    bearing: 0,
  });
  const [hoverInfo, setHoverInfo] = useState(null); // Tooltip state

  const mapboxApiAccessToken = 'YOUR_MAPBOX_ACCESS_TOKEN'; // Replace with your Mapbox token

  const EARTH_CIRCUMFERENCE = 40075000; // Earth's circumference in meters

  const calculateCellSize = (tileZoom) => {
    // Grid size dynamically scales with zoom level
    return EARTH_CIRCUMFERENCE / (256 * Math.pow(2, tileZoom));
  };

  const previousZoomLevel = useRef(viewState.zoom); // To track zoom changes
  const debounceTimer = useRef(null); // For debouncing

  // Generate initial snowflakes
  useEffect(() => {
    const generateSnowflakes = () => {
      const flakes = [];
      const numFlakes = 1000; // Adjust for density

      for (let i = 0; i < numFlakes; i++) {
        flakes.push({
          id: `north-${i}`,
          longitude: Math.random() * 360 - 180,
          latitude: 60 + Math.random() * 30,
          size: Math.random() * 3 + 1,
          fallSpeed: Math.random() * 0.1 + 0.05,
        });

        flakes.push({
          id: `south-${i}`,
          longitude: Math.random() * 360 - 180,
          latitude: -60 - Math.random() * 30,
          size: Math.random() * 3 + 1,
          fallSpeed: Math.random() * 0.1 + 0.05,
        });
      }

      return flakes;
    };

    setSnowflakes(generateSnowflakes());
  }, []);

  // Update snowflakes position
  useEffect(() => {
    const interval = setInterval(() => {
      setSnowflakes((prevFlakes) =>
        prevFlakes.map((flake) => {
          let newLatitude = flake.latitude - flake.fallSpeed;

          if (flake.latitude < 60 && flake.latitude > 0) newLatitude = 90;
          if (flake.latitude < -90) newLatitude = -60;

          return { ...flake, latitude: newLatitude };
        }),
      );
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Fly to a specific location or bounds
  useEffect(() => {
    if (flyTo) {
      const { longitude, latitude, bounds } = flyTo;
  
      if (bounds) {
        // If bounds are provided, calculate the viewport and use it
        const viewport = new WebMercatorViewport({
          width: window.innerWidth,
          height: window.innerHeight,
        }).fitBounds(bounds, { padding: 20 });
  
        setViewState((prevState) => ({
          ...prevState,
          longitude: viewport.longitude,
          latitude: viewport.latitude,
          zoom: viewport.zoom,
          pitch: prevState.pitch || 30, // Preserve the current pitch
          bearing: prevState.bearing || 0, // Preserve the current bearing
          transitionDuration: 1000, // 1 second fly-to animation
          transitionInterpolator: new FlyToInterpolator(),
        }));
      } else if (longitude !== undefined && latitude !== undefined) {
        // Fly to a specific point if bounds are not provided
        setViewState((prevState) => ({
          ...prevState,
          longitude,
          latitude,
          zoom: prevState.zoom || 12, // Keep the current zoom or default to 12
          pitch: prevState.pitch || 30, // Preserve the current pitch
          bearing: prevState.bearing || 0, // Preserve the current bearing
          transitionDuration: 1000, // 1 second fly-to animation
          transitionInterpolator: new FlyToInterpolator(),
        }));
      } else {
        console.warn('flyTo object is missing required properties');
      }
    }
  }, [flyTo]);    

  const handleViewStateChange = ({ viewState: newViewState }) => {
    setViewState(newViewState);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      // Calculate the actual tile zoom level using Web Mercator projection
      const viewport = new WebMercatorViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        longitude: newViewState.longitude,
        latitude: newViewState.latitude,
        zoom: newViewState.zoom,
      });

      const tileZoom = Math.floor(viewport.zoom); // Map tile zoom level is the integer part of the Mercator zoom
      console.log('Tile Zoom Level Calculated in MapView.js:', tileZoom);

      const [[minLng, minLat], [maxLng, maxLat]] = viewport.getBounds();
      const bounds = {
        topLeft: { lat: maxLat, lon: minLng },
        bottomRight: { lat: minLat, lon: maxLng },
      };

      // Trigger onMapChange only if the zoom level has changed
      if (tileZoom !== previousZoomLevel.current) {
        console.log('Debounced Tile Zoom:', tileZoom);
        onMapChange({ zoom: tileZoom, bounds }); // Pass integer tile zoom level
        previousZoomLevel.current = tileZoom;
      }
    }, 500); // Debounce delay
  };

  const santaLocationsLayer = santaLocations
    ? new ArcLayer({
        id: 'ArcLayer',
        data: santaLocations,
        getSourcePosition: (d) => d.from.coordinates,
        getTargetPosition: (d) => d.to.coordinates,
        getSourceColor: (d) => [140, Math.sqrt(d.inbound), 0],
        getTargetColor: (d) => [Math.sqrt(d.outbound), 140, 0],
        getWidth: 12,
        pickable: true,
      })
    : null;

  // Layers
  const layers = [
    santaLocationsLayer,
    // Snowflake animation
    new ScatterplotLayer({
      id: 'snow-layer',
      data: snowflakes,
      getPosition: (d) => [d.longitude, d.latitude],
      getRadius: (d) => d.size * 1000,
      getFillColor: [255, 255, 255, 200],
      radiusUnits: 'meters',
      opacity: 0.8,
      pickable: false, // disable hover interactions
    }),

    // Political boundary layer
    countryGeoJson &&
      new GeoJsonLayer({
        id: 'country-boundary-layer',
        data: countryGeoJson,
        stroked: true,
        filled: false,
        lineWidthMinPixels: 2,
        getLineColor: [0, 128, 0, 255],
      }),

    // Heatmap rendered as grid
    heatmapData &&
      heatmapData.length > 0 &&
      new HexagonLayer({
        id: 'heatmap-layer',
        data: heatmapData,
        getPosition: (d) => {
          const [z, x, y] = d.key.split('/').map(Number);
          const lon = tileToLon({ x, z });
          const lat = tileToLat({ y, z });
          return [lon, lat];
        },
        radius: calculateCellSize(tileZoom), // Set the radius dynamically based on tile zoom
        elevationScale: 100, // Adjust for desired cylinder height
        extruded: true, // Enable 3D extrusion
        colorRange: [
          [255, 0, 0], // Bright Red (lowest scores)
          [0, 100, 0], // Dark Green (low scores)
          [0, 150, 0], // Green (mid scores)
          [0, 200, 0], // Mid Green (high scores)
          [0, 255, 0], // Bright Green (highest scores)
        ],
        getColorValue: (points) =>
          points.reduce((acc, p) => acc + p.niceScore, 0) / points.length, // Average score per hexagon
        getElevationValue: (points) =>
          points.reduce((acc, p) => acc + p.niceScore, 0) / points.length, // Average elevation
        pickable: true, // Enable hover interactions
        onHover: (info) => {
          if (info.object) {
            const pointData = info.object.points[0];
            setHoverInfo({
              x: info.x,
              y: info.y,
              niceScore: pointData.source.niceScore || 0,
              cityName: pointData.source.cityName || 'Unknown',
            });
          } else {
            setHoverInfo(null);
          }
        },
        // Simulate edges by slightly darkening the fill
        coverage: 0.85, // Leave a small gap between hexagons to create a pseudo-border effect
      }),

    // new GridLayer({
    //   id: "heatmap-layer",
    //   data: heatmapData,
    //   cellSize: calculateCellSize(tileZoom), // Dynamic cell size
    //   getPosition: (d) => {
    //     try {
    //       const [z, x, y] = d.key.split("/").map(Number); // Extract zoom (z), x, and y
    //       const lon = tileToLon({ x, z }); // Use z as zoom level
    //       const lat = tileToLat({ y, z }); // Use z as zoom level

    //       if (isNaN(lon) || isNaN(lat)) {
    //         throw new Error(`Invalid coordinates for key: ${d.key}`);
    //       }

    //       return [lon, lat];
    //     } catch (err) {
    //       console.error("Error converting tile key:", d.key, err.message);
    //       return null; // Skip invalid entries
    //     }
    //   },
    //   colorRange: [
    //     [255, 0, 0],     // Bright Red (lowest scores)
    //     [0, 100, 0],     // Dark Green (low scores)
    //     [0, 150, 0],     // Green (mid scores)
    //     [0, 200, 0],     // Mid Green (high scores)
    //     [0, 255, 0],     // Bright Green (highest scores)
    //   ],

    //   getColorWeight: (d) => d.niceScore || 1, // Weight by niceScore
    //   colorAggregation: "MEAN",
    //   getElevationWeight: (d) => d.niceScore || 1,
    //   elevationAggregation: "MEAN",
    //   extruded: true,
    //   elevationScale: 100,
    //   pickable: true, // Enable hover interactions
    //   onHover: (info) => {
    //     if (info.object && info.object.points && info.object.points.length > 0) {
    //       const pointData = info.object.points[0]; // Access the first point in the array
    //       setHoverInfo({
    //         x: info.x,
    //         y: info.y,
    //         niceScore: pointData.source.niceScore || 0, // Extract niceScore
    //         cityName: pointData.source.cityName || "Unknown", // Extract cityName
    //       });
    //     } else {
    //       setHoverInfo(null); // Clear tooltip when not hovering
    //     }
    //   },
    // }),
  ].filter(Boolean);

  // Helper functions for tile conversions
  const tileToLon = ({ x, z }) => {
    return (x / Math.pow(2, z)) * 360 - 180;
  };

  const tileToLat = ({ y, z }) => {
    const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
    return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  };

  return (
    <DeckGL
      viewState={viewState}
      onViewStateChange={handleViewStateChange}
      controller={true}
      layers={layers}
    >
      <StaticMap
        mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        mapboxApiAccessToken={mapboxApiAccessToken}
      />

      {/* Tooltip for hover interactions */}
      {hoverInfo && (
        <div
          style={{
            position: 'absolute',
            left: hoverInfo.x + 10, // Offset slightly from the cursor
            top: hoverInfo.y + 10,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 999,
          }}
        >
          <div>
            <strong>City:</strong> {hoverInfo.cityName}
          </div>
          <div>
            <strong>Nice Score:</strong> {hoverInfo.niceScore.toFixed(2)}
          </div>
        </div>
      )}
    </DeckGL>
  );
}

export default MapView;
